import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePayerBankNameById } from '@modules/Wallets/BankAccounts/hooks/usePayerBanks';
import { resolvePayerBankName } from '@modules/Wallets/BankAccounts/lib/resolvePayerBankName';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadWithdrawalPaymentFile } from '../../hooks/useDownloadWithdrawalPaymentFile';
import type { WithdrawalPaymentFile } from '../../models';

export interface ViewPaymentFileModalRef {
  open: (file: WithdrawalPaymentFile) => void;
  close: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export const ViewPaymentFileModal = forwardRef<ViewPaymentFileModalRef>((_, ref) => {
  const { nameById } = usePayerBankNameById();
  const { t } = useTranslation('withdrawalPaymentFilesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [fileId, setFileId] = useState<number | null>(null);
  const [paymentFile, setPaymentFile] = useState<WithdrawalPaymentFile | null>(null);

  const { downloadFile, downloadingFileId } = useDownloadWithdrawalPaymentFile();

  useImperativeHandle(ref, () => ({
    open: (file) => {
      setFileId(file.id);
      setPaymentFile(file);
      modalRef.current?.openModal();
    },
    close: () => {
      setFileId(null);
      setPaymentFile(null);
      modalRef.current?.closeModal();
    },
  }));

  const isDownloading = downloadingFileId === fileId;

  return (
    <ModalCenter ref={modalRef} title={t('modals.view.title')} width="md">
      <div className="space-y-4">
        {paymentFile && (
          <>
            {/* Datos generales del fichero - General file data */}
            <div className="rounded-lg border border-lines bg-card-bg p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-subtext">{t('modals.view.fields.id')}</p>
                  <p className="font-medium text-title">#{paymentFile.id}</p>
                </div>
                <div className="sm:col-span-2 min-w-0">
                  <p className="text-subtext">{t('modals.view.fields.generation_id')}</p>
                  <p className="font-mono text-xs text-title break-all whitespace-normal leading-relaxed">
                    {paymentFile.generation_id}
                  </p>
                </div>
                <div>
                  <p className="text-subtext">{t('modals.view.fields.payer_bank')}</p>
                  <p className="font-medium text-title">
                    {resolvePayerBankName(paymentFile.payer_bank_id, paymentFile.payer_bank, nameById)}
                  </p>
                </div>
                <div>
                  <p className="text-subtext">{t('modals.view.fields.withdrawals_count')}</p>
                  <p className="font-medium text-title">
                    {paymentFile.withdrawals_count ?? paymentFile.withdrawal_count ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-subtext">{t('modals.view.fields.created_at')}</p>
                  <p className="font-medium text-title">{formatDate(paymentFile.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => modalRef.current?.closeModal()}
                data-testid="close-payment-file-detail"
              >
                {t('buttons.close')}
              </Button>
              <Button
                variant="primary"
                onClick={() => fileId && downloadFile(fileId)}
                disabled={isDownloading}
                data-testid="download-payment-file-detail"
              >
                <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5 mr-1.5" />
                {isDownloading ? t('actions.downloading') : t('actions.download')}
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalCenter>
  );
});

ViewPaymentFileModal.displayName = 'ViewPaymentFileModal';
